import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { ticketRef } from "utilities/ticketNumber";
import { useParams, useNavigate } from "react-router-dom";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth, requireAuth } from "utilities/auth";
import { isInternal, isPartner } from "utilities/userType";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { useInternalUsers } from "hooks/crm/useTickets";
import { useToast } from "components/Toast";
import PageHeader from "components/PageLayout";
import Skeleton from "components/Skeleton";
import UserAvatar from "components/UserAvatar";
import RelativeTime from "components/RelativeTime";
import TicketStatusPill from "components/Tickets/TicketStatusPill";
import PriorityIndicator from "components/Tickets/PriorityIndicator";
import ConversationThread from "components/Tickets/ConversationThread";
import TicketIntakeDetails from "components/Tickets/TicketIntakeDetails";
import MessageComposer from "components/Tickets/MessageComposer";
import ActivityTimeline from "components/Tickets/ActivityTimeline";
import WithdrawButton from "components/Tickets/WithdrawButton";
import EntityLinkPanel from "components/Tickets/EntityLinkPanel";
import RelationsPanel from "components/Tickets/RelationsPanel";
import WatchersPanel from "components/Tickets/WatchersPanel";
import SlaPanel from "components/Tickets/SlaPanel";
import DependencyBlockerNotice from "components/Tickets/DependencyBlockerNotice";
import TicketCreateModal from "components/Tickets/TicketCreateModal";
import { useSlaPolicies } from "hooks/useSlaPolicies";
import { useTicketTemplates } from "hooks/useTicketTemplates";
import { ticketVariantDisplay } from "utilities/ticketVariants";
import { useFunctions } from "hooks/useFunctions";
import FunctionChip from "components/Functions/FunctionChip";
import {
  describeFunctionTarget,
  filterFunctionTargets,
  filterUserTargets,
  assignedViaMessage,
  functionLabel as resolveFunctionLabel,
} from "utilities/assignTargets";
import {
  countOpenSubtasks,
  cascadePrompt,
  summarizeCascade,
} from "utilities/cancelCascade";
import {
  TICKET_STATUS_LABELS,
  TICKET_STATUS_CATEGORY,
  TICKET_TRANSITIONS,
  TICKET_STATUS_NEEDS_REASON,
} from "constants/tickets";
import {
  ArrowLeft,
  ChevronDown,
  MessageSquare,
  RotateCw,
  Search,
  Plus,
  UserPlus,
  Ticket as TicketIcon,
  Info,
  Briefcase,
  Scale,
} from "lucide-react";

function humanize(key) {
  if (!key) return "";
  return String(key)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function displayNameOf(u = {}) {
  return (
    u.name ||
    [u.firstName, u.lastName].filter(Boolean).join(" ") ||
    u.email ||
    "User"
  );
}

function Ticket() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const internal = isInternal(user);
  // SOUND-611: partners manage several clients, so a ticket detail with no client
  // name is ambiguous for them too — consistent with their inbox Client column.
  const partner = isPartner(user);
  const effectiveCompanyId = useEffectiveCompanyId();
  const { toast } = useToast();

  const authUser = { id: user.id, accessToken: user.accessToken };
  const meName = displayNameOf(user);

  // Internal-user directory (SOUND-526) — shared source for the @-mention
  // autocomplete, the watcher picker, and mention-chip name resolution. Fetched
  // once (small team) and filtered client-side. Internal-only.
  const { users: internalUsers } = useInternalUsers({ enabled: internal });
  const mentionDirectory = useMemo(() => {
    const map = {};
    for (const u of internalUsers) map[u.id || u._id] = u;
    return map;
  }, [internalUsers]);

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState(null);

  // SLA policy directory (SOUND-534) — resolve the ticket's stamped policyId to
  // a human name for the right-rail SLA panel. Internal-only config surface.
  // includeInactive so a ticket stamped with a since-deactivated policy still
  // resolves its name (deactivated policies stay resolvable for history).
  // Job-function registry (SOUND-667) — powers the Assign menu's function
  // section AND resolves the ticket's own assigneeRole to a label for the
  // Properties chip. `include: "holders"` returns the resolved defaultOwner +
  // holders in ONE call, which is what lets each row say where it will land.
  const { functions: jobFunctions } = useFunctions({
    include: "holders",
    enabled: internal,
  });

  const { policies: slaPolicies } = useSlaPolicies({ enabled: internal, includeInactive: true });
  // The ticket's own job function, resolved to a label for the Properties chip.
  // Falls back to the raw key so a ticket stamped with a since-renamed function
  // still says something.
  const assigneeRoleLabel = useMemo(
    () => resolveFunctionLabel(jobFunctions, ticket?.assigneeRole),
    [jobFunctions, ticket]
  );

  // SOUND-785 — the workflow variant this ticket ran, shown only when it says
  // something (the type has 2+ variants, or this one is not "default"); rule in
  // utilities/ticketVariants.ticketVariantDisplay. Templates are internal-only.
  const ticketRequestType = ticket?.requestType || "";
  const templateParams = useMemo(() => ({ requestType: ticketRequestType }), [ticketRequestType]);
  const { templates: typeTemplates } = useTicketTemplates(templateParams, {
    enabled: internal && !!ticketRequestType,
  });
  const variantInfo = useMemo(
    () =>
      internal && ticket
        ? ticketVariantDisplay(ticket, typeTemplates, ticket.requestTypeLabel || humanize(ticket.requestType))
        : null,
    [internal, ticket, typeTemplates]
  );

  const slaPolicyName = useMemo(() => {
    const id = ticket?.sla?.policyId;
    if (!id) return null;
    return slaPolicies.find((p) => p.policyId === id)?.name || null;
  }, [slaPolicies, ticket]);

  const [statusSubmitting, setStatusSubmitting] = useState(false);
  // functionKey currently being resolved by the server (SOUND-667) — the
  // function path cannot be optimistic, so the row shows it is working.
  const [assigningFunction, setAssigningFunction] = useState(null);
  // SOUND-593 — the "New sub-task" modal (TicketCreateModal in sub-task mode).
  const [subtaskOpen, setSubtaskOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusReason, setStatusReason] = useState("");

  // Depends-on gating (Phase 2): when a done-transition 409s, we hold the
  // blocker list + the attempted target/reason so the notice can offer a
  // governed override without re-collecting them.
  const [blockers, setBlockers] = useState(null);
  const [blockedTarget, setBlockedTarget] = useState(null);
  const [blockedReason, setBlockedReason] = useState("");

  const fetchTicket = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const res = await makeApiRequest(
          "get",
          apiRoutes.getTicketById(ticketId),
          authUser,
          effectiveCompanyId
        );
        setTicket(res.data?.data || null);
        setErrorStatus(null);
      } catch (err) {
        setErrorStatus(err.response?.status || 500);
        if (!silent) setTicket(null);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ticketId, effectiveCompanyId]
  );

  useEffect(() => {
    if (ticketId) fetchTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, effectiveCompanyId]);

  // ---- Status change (optimistic for non-gated; pessimistic for done) ----
  // Done-category transitions (resolved/declined) may be gated by open
  // depends_on blockers → 409 { blockers }. To avoid a flip-then-unflip flicker
  // we DON'T optimistically flip those; a 409 surfaces the blocker notice
  // instead. `override` (internal) retries with override:{reason} to proceed.
  const commitStatus = async (target, reason, override) => {
    if (!ticket) return;
    const prev = ticket;
    const category = TICKET_STATUS_CATEGORY[target];
    const isDone = category === "done";
    const nowIso = new Date().toISOString();
    const synthetic = {
      eventId: `tmp-s-${Date.now()}`,
      type: "status_changed",
      actor: { displayName: meName },
      data: {
        before: { status: prev.status, category: prev.statusCategory },
        after: { status: target, category },
        reason: reason || undefined,
      },
      createdAt: nowIso,
    };
    const flipped = {
      ...prev,
      status: target,
      statusCategory: category,
      updatedAt: nowIso,
      events: [...(prev.events || []), synthetic],
    };

    if (!isDone) setTicket(flipped); // optimistic for non-gated transitions
    setStatusSubmitting(true);
    try {
      const res = await makeApiRequest(
        "put",
        apiRoutes.changeTicketStatus(ticketId),
        authUser,
        effectiveCompanyId,
        {
          status: target,
          reason: reason || undefined,
          ...(override ? { override: { reason: override } } : {}),
        }
      );
      if (isDone) setTicket(flipped); // reflect after the gate clears

      // SOUND-576 — report the cascade. `meta` is present only on a
      // cancel/withdraw. A PARTIAL cascade must not read as a clean success: the
      // ticket was cancelled but some sub-tasks were not, and someone has to know.
      const statusTitle = `Status → ${TICKET_STATUS_LABELS[target] || target}`;
      const cascade = summarizeCascade(res?.data?.meta);
      toast({
        status: cascade?.tone || "success",
        title: cascade?.title || statusTitle,
        ...(cascade?.description ? { description: cascade.description } : {}),
      });
      setBlockers(null);
      setBlockedTarget(null);
      setBlockedReason("");
      setPendingStatus(null);
      setStatusReason("");
      await fetchTicket({ silent: true });
    } catch (err) {
      if (!isDone) setTicket(prev); // rollback optimistic
      if (err.response?.status === 409 && Array.isArray(err.response?.data?.blockers)) {
        // Depends-on gate: hold the blockers + attempted target for the notice.
        setBlockers(err.response.data.blockers);
        setBlockedTarget(target);
        setBlockedReason(reason || "");
        setPendingStatus(null);
        setStatusReason("");
      } else {
        toast({
          status: "destructive",
          title: "Failed to change status",
          description: err.response?.data?.error || "Unknown error",
        });
      }
    } finally {
      setStatusSubmitting(false);
    }
  };

  const onPickStatus = (target) => {
    if (TICKET_STATUS_NEEDS_REASON.has(target)) {
      setPendingStatus(target);
      setStatusReason("");
    } else {
      commitStatus(target);
    }
  };

  // ---- Assignment (optimistic + rollback) ----
  const handleAssign = async (assignee) => {
    if (!ticket) return;
    const prev = ticket;
    const snapshot = assignee
      ? {
          kind: "user",
          userId: assignee.userId,
          email: assignee.email,
          displayName: assignee.displayName,
        }
      : null;
    const synthetic = {
      eventId: `tmp-a-${Date.now()}`,
      type: "assigned",
      actor: { displayName: meName },
      data: { before: prev.assignee || null, after: snapshot },
      createdAt: new Date().toISOString(),
    };
    setTicket({
      ...prev,
      assignee: snapshot,
      updatedAt: new Date().toISOString(),
      events: [...(prev.events || []), synthetic],
    });
    try {
      await makeApiRequest(
        "put",
        apiRoutes.changeTicketAssignee(ticketId),
        authUser,
        effectiveCompanyId,
        { assigneeId: assignee?.userId || null }
      );
      toast({
        status: "success",
        title: assignee ? `Assigned to ${assignee.displayName}` : "Unassigned",
      });
      await fetchTicket({ silent: true });
    } catch (err) {
      setTicket(prev); // rollback
      toast({
        status: "destructive",
        title: "Failed to update assignee",
        description: err.response?.data?.error || "Unknown error",
      });
    }
  };

  // ---- Assignment to a JOB FUNCTION (SOUND-667) ----
  // Deliberately NOT optimistic. A hand-pick already knows the person, so it can
  // paint first and reconcile; a function assignment does not — the SERVER picks
  // (default owner, or least-loaded holder), and inventing a name here would
  // flash the wrong face. The row shows a pending state instead and we reconcile
  // from the response.
  const handleAssignFunction = async (fn) => {
    if (!ticket || !fn?.functionKey) return;
    setAssigningFunction(fn.functionKey);
    try {
      const res = await makeApiRequest(
        "put",
        apiRoutes.changeTicketAssignee(ticketId),
        authUser,
        effectiveCompanyId,
        { functionKey: fn.functionKey }
      );
      const assignee = res.data?.data?.assignee || null;
      toast({ status: "success", title: assignedViaMessage(assignee, fn.label) });
      await fetchTicket({ silent: true });
    } catch (err) {
      // The API's 400s are written to be read by a human ("Billing has no
      // default owner or active holders") and tell them what to go fix, so
      // surface the message verbatim rather than a generic failure.
      toast({
        status: "destructive",
        title: `Could not assign to ${fn.label}`,
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setAssigningFunction(null);
    }
  };

  // ---- Comments (optimistic append; errored bubble + retry on failure) ----
  const postComment = async (tempId, body, visibility, mentions) => {
    try {
      await makeApiRequest(
        "post",
        apiRoutes.addTicketComment(ticketId),
        authUser,
        effectiveCompanyId,
        { body, visibility, ...(mentions?.length ? { mentions } : {}) }
      );
      await fetchTicket({ silent: true });
    } catch (err) {
      setTicket((prev) =>
        prev
          ? {
              ...prev,
              messages: (prev.messages || []).map((m) =>
                m.messageId === tempId ? { ...m, _optimistic: false, _error: true } : m
              ),
            }
          : prev
      );
      toast({
        status: "destructive",
        title: "Message failed to send",
        description: err.response?.data?.error || "Unknown error",
      });
    }
  };

  const handleSend = (body, visibility, mentions = []) => {
    const tempId = `tmp-m-${Date.now()}`;
    // Resolve mentioned userIds to {userId,name,email} so chips render instantly
    // on the optimistic bubble (the silent refetch reconciles to server shape).
    const optimisticMentions = mentions
      .map((id) => {
        const u = mentionDirectory[id];
        return u
          ? { userId: id, name: displayNameOf(u), email: u.email }
          : { userId: id };
      });
    const optimistic = {
      messageId: tempId,
      body,
      visibility,
      mentions: optimisticMentions,
      createdAt: new Date().toISOString(),
      author: { kind: "user", userId: user.id, email: user.email, displayName: meName },
      _optimistic: true,
      _mentions: mentions,
    };
    setTicket((prev) =>
      prev ? { ...prev, messages: [...(prev.messages || []), optimistic] } : prev
    );
    postComment(tempId, body, visibility, mentions);
    return Promise.resolve(); // composer clears immediately (feels instant)
  };

  const handleRetry = (message) => {
    setTicket((prev) =>
      prev
        ? {
            ...prev,
            messages: (prev.messages || []).map((m) =>
              m.messageId === message.messageId
                ? { ...m, _error: false, _optimistic: true }
                : m
            ),
          }
        : prev
    );
    postComment(
      message.messageId,
      message.body,
      message.visibility,
      message._mentions || (message.mentions || []).map((mv) => (typeof mv === "object" ? mv.userId : mv))
    );
  };

  // ---- Entity links (optimistic + rollback) ----
  const addLink = async ({ entityType, entityId, label, role }) => {
    if (!ticket) return;
    const prev = ticket;
    const tempId = `tmp-l-${Date.now()}`;
    setTicket({
      ...prev,
      links: [
        ...(prev.links || []),
        { linkId: tempId, entityType, entityId, label, role, _optimistic: true },
      ],
    });
    try {
      const res = await makeApiRequest(
        "post",
        apiRoutes.addTicketLink(ticketId),
        authUser,
        effectiveCompanyId,
        { entityType, entityId, label, role }
      );
      // SOUND-611: the link now propagates to existing sub-tickets. Say so —
      // otherwise a change touching several tickets looks like it touched one.
      const applied = res?.data?.meta?.propagatedToChildren || 0;
      toast({
        status: "success",
        title: "Entity linked",
        ...(applied
          ? {
              description: `Also applied to ${applied} sub-ticket${applied === 1 ? "" : "s"}.`,
            }
          : {}),
      });
      await fetchTicket({ silent: true });
    } catch (err) {
      setTicket(prev);
      toast({
        status: "destructive",
        title: "Failed to link entity",
        description: err.response?.data?.error || "Unknown error",
      });
      throw err;
    }
  };

  const removeLink = async (linkId) => {
    if (!ticket) return;
    const prev = ticket;
    setTicket({ ...prev, links: (prev.links || []).filter((l) => l.linkId !== linkId) });
    try {
      const res = await makeApiRequest(
        "delete",
        apiRoutes.removeTicketLink(ticketId, linkId),
        authUser,
        effectiveCompanyId
      );
      const retracted = res?.data?.meta?.retractedFromChildren || 0;
      toast({
        status: "success",
        title: "Link removed",
        ...(retracted
          ? {
              description: `Also removed from ${retracted} sub-ticket${retracted === 1 ? "" : "s"}.`,
            }
          : {}),
      });
      await fetchTicket({ silent: true });
    } catch (err) {
      setTicket(prev);
      toast({
        status: "destructive",
        title: "Failed to remove link",
        description: err.response?.data?.error || "Unknown error",
      });
    }
  };

  // ---- Watchers (SOUND-526; optimistic + reconcile from POST response) ----
  const watchSelf = async () => {
    if (!ticket) return;
    const prev = ticket;
    const optimistic = { userId: user.id, name: meName, email: user.email };
    setTicket({ ...prev, watchers: [...(prev.watchers || []), optimistic] });
    try {
      const res = await makeApiRequest(
        "post",
        apiRoutes.addTicketWatcher(ticketId),
        authUser,
        effectiveCompanyId,
        {} // omit userId → self-watch
      );
      const watchers = res.data?.data?.watchers;
      if (Array.isArray(watchers)) setTicket((t) => (t ? { ...t, watchers } : t));
      toast({ status: "success", title: "Watching this ticket" });
    } catch (err) {
      setTicket(prev);
      toast({
        status: "destructive",
        title: "Failed to watch ticket",
        description: err.response?.data?.error || "Unknown error",
      });
    }
  };

  const addWatcher = async (userId) => {
    if (!ticket || !userId) return;
    const prev = ticket;
    if ((prev.watchers || []).some((w) => w.userId === userId)) return;
    const u = mentionDirectory[userId];
    const optimistic = { userId, name: u ? displayNameOf(u) : userId, email: u?.email };
    setTicket({ ...prev, watchers: [...(prev.watchers || []), optimistic] });
    try {
      const res = await makeApiRequest(
        "post",
        apiRoutes.addTicketWatcher(ticketId),
        authUser,
        effectiveCompanyId,
        { userId }
      );
      const watchers = res.data?.data?.watchers;
      if (Array.isArray(watchers)) setTicket((t) => (t ? { ...t, watchers } : t));
      toast({ status: "success", title: "Watcher added" });
    } catch (err) {
      setTicket(prev);
      toast({
        status: "destructive",
        title: "Failed to add watcher",
        description: err.response?.data?.error || "Unknown error",
      });
    }
  };

  const removeWatcher = async (userId) => {
    if (!ticket || !userId) return;
    const prev = ticket;
    setTicket({ ...prev, watchers: (prev.watchers || []).filter((w) => w.userId !== userId) });
    try {
      await makeApiRequest(
        "delete",
        apiRoutes.removeTicketWatcher(ticketId, userId),
        authUser,
        effectiveCompanyId
      );
      toast({
        status: "success",
        title: userId === user.id ? "Stopped watching" : "Watcher removed",
      });
    } catch (err) {
      setTicket(prev);
      toast({
        status: "destructive",
        title: "Failed to remove watcher",
        description: err.response?.data?.error || "Unknown error",
      });
    }
  };

  // ---- Relations (optimistic + rollback) ----
  const addRelation = async ({ toTicketId, type, summary }) => {
    if (!ticket) return;
    const prev = ticket;
    const tempId = `tmp-r-${Date.now()}`;
    setTicket({
      ...prev,
      relations: {
        outgoing: [
          ...(prev.relations?.outgoing || []),
          { relationId: tempId, fromTicketId: ticketId, toTicketId, type, _optimistic: true, _summary: summary },
        ],
        incoming: prev.relations?.incoming || [],
      },
    });
    try {
      await makeApiRequest("post", apiRoutes.addTicketRelation(ticketId), authUser, effectiveCompanyId, {
        toTicketId,
        type,
      });
      toast({ status: "success", title: "Relation added" });
      await fetchTicket({ silent: true });
    } catch (err) {
      setTicket(prev);
      const msg = err.response?.data?.error || "Unknown error";
      toast({
        status: "destructive",
        title: msg === "Dependency cycle" ? "Dependency cycle" : "Failed to add relation",
        description: msg === "Dependency cycle" ? "That would create a circular dependency." : msg,
      });
      throw err;
    }
  };

  const removeRelation = async (relationId) => {
    if (!ticket) return;
    const prev = ticket;
    setTicket({
      ...prev,
      relations: {
        outgoing: (prev.relations?.outgoing || []).filter((r) => r.relationId !== relationId),
        incoming: (prev.relations?.incoming || []).filter((r) => r.relationId !== relationId),
      },
    });
    try {
      await makeApiRequest("delete", apiRoutes.removeTicketRelation(ticketId, relationId), authUser, effectiveCompanyId);
      toast({ status: "success", title: "Relation removed" });
      await fetchTicket({ silent: true });
    } catch (err) {
      setTicket(prev);
      toast({
        status: "destructive",
        title: "Failed to remove relation",
        description: err.response?.data?.error || "Unknown error",
      });
    }
  };

  // ---- Parent / child (optimistic + rollback) ----
  const setParent = async (parentTicketId, summary) => {
    if (!ticket) return;
    const prev = ticket;
    setTicket({
      ...prev,
      parentTicketId: parentTicketId || null,
      _parentSummary: parentTicketId ? summary : null,
    });
    try {
      await makeApiRequest("put", apiRoutes.setTicketParent(ticketId), authUser, effectiveCompanyId, {
        parentTicketId: parentTicketId || null,
      });
      toast({ status: "success", title: parentTicketId ? "Parent set" : "Parent cleared" });
      await fetchTicket({ silent: true });
    } catch (err) {
      setTicket(prev);
      toast({
        status: "destructive",
        title: "Failed to set parent",
        description: err.response?.data?.error || "Unknown error",
      });
      throw err;
    }
  };

  // ---- Loading / error states ----
  if (loading) {
    return (
      <div>
        <PageHeader title={<Skeleton.Rect className="h-8 w-72" />} sticky />
        <div className="container grid grid-cols-1 gap-6 py-6 lg:grid-cols-[1fr_320px]">
          <div className="space-y-4">
            <Skeleton.Rect className="h-24 w-full" />
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex gap-3">
                <Skeleton.Circle size={32} />
                <div className="flex-1">
                  <Skeleton.Text lines={2} />
                </div>
              </div>
            ))}
          </div>
          <Skeleton.Rect className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="p-10 text-center">
        <TicketIcon className="mx-auto h-12 w-12 text-muted-foreground" />
        <h3 className="mt-3 text-lg font-medium text-foreground">
          {errorStatus === 403 ? "You don't have access to this ticket" : "Ticket not found"}
        </h3>
        <button className="btn btn-ghost mt-4" onClick={() => navigate("/tickets")}>
          <ArrowLeft className="h-4 w-4" />
          Back to Tickets
        </button>
      </div>
    );
  }

  const category = ticket.statusCategory || TICKET_STATUS_CATEGORY[ticket.status];
  const canWithdraw = category === "todo" || category === "in_progress";
  // SOUND-593 — mirrors the backend's two refusals so the header never offers an
  // action that can only 400. Keys on CATEGORY, never a raw status name.
  const isClosedTicket = category === "done" || category === "canceled";
  const canAddSubtask = internal && !ticket.parentTicketId && !isClosedTicket;
  // SOUND-576 — how many sub-tasks a cancel/withdraw would take down with it.
  // Derived LOCALLY from ticket.children (already on the detail response), so the
  // warning costs no extra request. Logic lives in utilities/cancelCascade.js so
  // it is unit-tested rather than click-verified.
  const cascadeNotice = cascadePrompt(countOpenSubtasks(ticket.children));
  const companyName = ticket.companyName || ticket.company?.name || "";
  const requestTypeLabel = ticket.requestTypeLabel || humanize(ticket.requestType);
  const requester = ticket.requester || ticket.createdBy || null;
  const backLabel = internal ? "Back to Tickets" : "Back to Support";

  return (
    <div>
      <PageHeader
        sticky
        title={
          <span className="inline-flex items-baseline gap-2">
            <span className="font-mono text-muted-foreground">{ticketRef(ticket.number)}</span>
            <span>{ticket.title}</span>
          </span>
        }
        badge={<TicketStatusPill status={ticket.status} statusCategory={ticket.statusCategory} />}
        subtitle={
          <button
            onClick={() => navigate("/tickets")}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {backLabel}
          </button>
        }
        meta={[
          // SOUND-581 — client + MID in the header, which renders on EVERY
          // ticket including sub-tasks. Previously only the parent carried this,
          // so a person working a sub-task had to navigate up to learn which
          // merchant and account it concerned. Partners manage multiple clients,
          // so they need it too; a client only ever sees their own.
          ...(companyName && (internal || partner)
            ? [{ label: "Client", value: companyName }]
            : []),
          ...(ticket.midLabel && (internal || partner)
            ? [{ label: "MID", value: ticket.midLabel }]
            : []),
          ...(ticket.requestType ? [{ label: "Request type", value: requestTypeLabel }] : []),
          ...(ticket.kind === "task" ? [{ label: "Kind", value: "Task" }] : []),
        ]}
        actions={
          internal ? (
            <>
              {/* SOUND-593 — sub-task creation lives in the STICKY header next to
                  Status/Assignee, not in the right rail. This is a primary action
                  (the main way a Telegram-filed ticket gets structured), and in
                  the rail it required scrolling past the whole conversation to
                  reach. Hidden — not disabled — when it can't apply: a sub-task
                  can't nest further, and a closed ticket can't take new open work
                  (the Sub-tickets group carries the "reopen first" explanation, so
                  the header stays uncluttered). */}
              {canAddSubtask && (
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setSubtaskOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                  Sub-task
                </button>
              )}
              <StatusControl status={ticket.status} onPick={onPickStatus} disabled={statusSubmitting} />
              <AssigneeMenu
                assignee={ticket.assignee}
                authUser={authUser}
                companyId={effectiveCompanyId}
                onAssign={handleAssign}
                functions={jobFunctions}
                onAssignFunction={handleAssignFunction}
                assigningFunction={assigningFunction}
              />
            </>
          ) : (
            canWithdraw && (
              <WithdrawButton onWithdraw={() => commitStatus("withdrawn")} submitting={statusSubmitting} />
            )
          )
        }
      />

      <div className="container py-6">
        {/* Depends-on blocker gate (replaces the optimistic flip on a 409) */}
        {blockers && (
          <DependencyBlockerNotice
            blockers={blockers}
            targetLabel={TICKET_STATUS_LABELS[blockedTarget] || blockedTarget}
            isInternal={internal}
            submitting={statusSubmitting}
            onOverride={(overrideReason) => commitStatus(blockedTarget, blockedReason, overrideReason)}
            onDismiss={() => {
              setBlockers(null);
              setBlockedTarget(null);
              setBlockedReason("");
            }}
          />
        )}

        {/* Reason bar (internal — waiting/declined/canceled/withdrawn). Doubles as
            the confirm gate for the cascading cancel-category transitions. */}
        {pendingStatus && (
          <div className="surface mb-6 flex animate-fade-in flex-col gap-3 p-4 sm:flex-row sm:items-center">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                Move to
                <TicketStatusPill status={pendingStatus} />
              </div>
              {/* SOUND-576 — state the consequence BEFORE the destructive confirm.
                  Internal-only: clients can't see sub-tasks at all, so naming our
                  internal decomposition to a merchant would leak it and mean
                  nothing to them. `canceled` has no reopen path, so this is the
                  last chance to reconsider. */}
              {internal &&
                TICKET_STATUS_CATEGORY[pendingStatus] === "canceled" &&
                cascadeNotice && <p className="text-xs text-warning">{cascadeNotice}</p>}
            </div>
            <input
              autoFocus
              className="input flex-1"
              placeholder={
                pendingStatus === "waiting"
                  ? "What are we waiting on? (optional)"
                  : "Reason (optional)"
              }
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && commitStatus(pendingStatus, statusReason)}
            />
            <div className="flex gap-2">
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setPendingStatus(null);
                  setStatusReason("");
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => commitStatus(pendingStatus, statusReason)}
                disabled={statusSubmitting}
              >
                Confirm
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[1fr_320px]">
          {/* CENTER */}
          <div className="min-w-0 space-y-5">
            {/* Client reassurance note */}
            {!internal && canWithdraw && (
              <div className="flex items-start gap-2.5 rounded-lg border border-brand/30 bg-brand/[0.08] px-4 py-3 text-sm">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                <span>
                  We're on it — the Sound team is working this now. You'll get an update here, and you
                  can reply anytime.
                </span>
              </div>
            )}

            {/* Description */}
            {ticket.description && (
              <section className="surface p-5">
                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {internal ? "Description" : "Your request"}
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                  {ticket.description}
                </p>
              </section>
            )}

            {/* Request details — collected template intake values (SOUND-510) */}
            <TicketIntakeDetails ticket={ticket} />

            {/* Conversation */}
            <div>
              <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Conversation
              </div>
              {ticket.messages && ticket.messages.length > 0 ? (
                <ConversationThread
                  messages={ticket.messages}
                  currentUserId={user.id}
                  isInternalViewer={internal}
                  onRetry={handleRetry}
                  mentionDirectory={mentionDirectory}
                />
              ) : (
                <div className="rounded-lg border border-dashed border-border py-8 text-center">
                  <MessageSquare className="mx-auto h-8 w-8 text-muted-foreground" />
                  <p className="mt-2 text-sm font-medium text-foreground">No messages yet</p>
                  <p className="text-sm text-muted-foreground">Start the conversation below.</p>
                </div>
              )}
            </div>

            {/* Composer (sticky) */}
            <MessageComposer
              className="sticky bottom-4"
              onSend={handleSend}
              allowInternal={internal}
              mentionUsers={internal ? internalUsers : []}
              replyToLabel={internal ? companyName || "the client" : "the Sound team"}
            />
          </div>

          {/* RIGHT RAIL */}
          <div className="space-y-4 lg:sticky lg:top-32">
            {/* Properties */}
            <section className="surface p-4">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {internal ? "Properties" : "Details"}
              </h3>
              <div className="divide-y divide-border/60">
                <Prop label="Status">
                  <TicketStatusPill status={ticket.status} statusCategory={ticket.statusCategory} />
                </Prop>
                {internal && (
                  <Prop label="Priority">
                    <PriorityIndicator priority={ticket.priority} />
                  </Prop>
                )}
                {internal && (
                  <Prop label="Assignee">
                    {/* SOUND-667: the job function shows alongside the person.
                        `assigneeRole` has been written on every workflow-spawned
                        ticket since SOUND-503 (549 of 765 in prod) and had no
                        surface at all — so a ticket that reached its owner
                        through Underwriting looked identical to a hand-pick. */}
                    <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                      {ticket.assignee ? (
                        <span className="inline-flex min-w-0 items-center gap-2">
                          <UserAvatar
                            user={{
                              name: ticket.assignee.displayName,
                              email: ticket.assignee.email,
                            }}
                          />
                          <span className="truncate text-sm">{ticket.assignee.displayName}</span>
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Unassigned</span>
                      )}
                      {assigneeRoleLabel && <FunctionChip label={assigneeRoleLabel} />}
                    </span>
                  </Prop>
                )}
                {internal && requester && (
                  <Prop label="Requester">
                    <span className="text-sm">{displayNameOf(requester)}</span>
                  </Prop>
                )}
                {internal && companyName && (
                  <Prop label="Company">
                    <button
                      className="text-sm font-semibold text-brand hover:underline"
                      onClick={() => navigate(`/companies/${ticket.companyId}`)}
                    >
                      {companyName} →
                    </button>
                  </Prop>
                )}
                {internal && (
                  <Prop label="Kind">
                    <span className="text-sm capitalize">{ticket.kind || "ticket"}</span>
                  </Prop>
                )}
                {ticket.requestType && (
                  <Prop label="Request">
                    <span className="text-sm">{requestTypeLabel}</span>
                  </Prop>
                )}
                {variantInfo && (
                  <Prop label="Variant">
                    <span className="text-sm" title={variantInfo.variantKey}>
                      {variantInfo.label}
                    </span>
                  </Prop>
                )}
                <Prop label="Opened">
                  <span className="text-sm text-muted-foreground">
                    <RelativeTime value={ticket.createdAt} />
                  </span>
                </Prop>
                <Prop label="Updated">
                  <span className="text-sm text-muted-foreground">
                    <RelativeTime value={ticket.updatedAt} />
                  </span>
                </Prop>
              </div>
            </section>

            {/* SLA (SOUND-534) — internal-only. Policy, both clocks (target +
                due + elapsed-vs-due), first-response status, paused state.
                Renders nothing when the ticket has no SLA policy. */}
            {internal && <SlaPanel ticket={ticket} policyName={slaPolicyName} />}

            {/* Watchers (SOUND-526) — internal-only. Self Watch/Unwatch toggle
                + directory picker to add/remove teammates. */}
            {internal && (
              <WatchersPanel
                watchers={ticket.watchers || []}
                currentUserId={user.id}
                directory={internalUsers}
                onWatchSelf={watchSelf}
                onAddWatcher={addWatcher}
                onRemoveWatcher={removeWatcher}
              />
            )}

            {/* Linked entities + relations (internal-only add/remove; the
                detail response already scope-strips for non-internal). */}
            {internal && (
              <EntityLinkPanel
                links={ticket.links || []}
                companyId={ticket.companyId}
                isInternal
                onAddLink={addLink}
                onRemoveLink={removeLink}
              />
            )}
            {internal && (
              <RelationsPanel
                ticket={ticket}
                isInternal
                onAddRelation={addRelation}
                onRemoveRelation={removeRelation}
                onSetParent={setParent}
              />
            )}

            {/* Activity */}
            <section className="surface p-4">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Activity
              </h3>
              <ActivityTimeline events={ticket.events || []} />
            </section>

            {/* Reserved — later phases */}
            <div className="rounded-lg border border-dashed border-border px-4 py-3 text-xs text-muted-foreground">
              <span className="mb-0.5 block font-semibold text-foreground/80">
                Reserved — later phases
              </span>
              {internal
                ? "Workflow steps attach here."
                : "Your team at Sound will keep this updated. If it's urgent, mention it in a reply."}
            </div>
          </div>
        </div>
      </div>

      {/* SOUND-593 — sub-task creation reuses the SHARED create modal in
          sub-task mode rather than a second bespoke form: one surface, so a
          sub-task can carry a description, priority and assignee like any other
          work item. The modal owns submission + its own toasts; we just close
          and refetch so the new child appears in the Sub-tickets group. */}
      {internal && (
        <TicketCreateModal
          open={subtaskOpen}
          onClose={() => setSubtaskOpen(false)}
          subtaskParent={{
            ticketId: ticket.ticketId,
            number: ticket.number,
            title: ticket.title,
            companyId: ticket.companyId,
            companyName: companyName || undefined,
            priority: ticket.priority,
          }}
          onCreated={async () => {
            setSubtaskOpen(false);
            await fetchTicket({ silent: true });
          }}
        />
      )}
    </div>
  );
}

function Prop({ label, children }) {
  return (
    <div className="grid grid-cols-[84px_1fr] items-center gap-2 py-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="flex items-center">{children}</span>
    </div>
  );
}

/* ---- Status control (internal): Reopen / legal-transition menu / Closed ---- */
function StatusControl({ status, onPick, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (status === "resolved" || status === "declined") {
    return (
      <button className="btn btn-outline" disabled={disabled} onClick={() => onPick("in_progress")}>
        <RotateCw className="h-4 w-4" />
        Reopen
      </button>
    );
  }

  const transitions = TICKET_TRANSITIONS[status] || [];
  if (!transitions.length) {
    return <span className="badge badge-muted">Closed</span>;
  }

  return (
    <div className="relative" ref={ref}>
      <button
        className="btn btn-outline"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        Status
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] z-30 min-w-[220px] animate-scale-in rounded-lg border border-border bg-card p-1.5 shadow-lg"
        >
          <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Move to — legal transitions
          </div>
          {transitions.map((target) => (
            <button
              key={target}
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onPick(target);
              }}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-muted"
            >
              <TicketStatusPill status={target} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- Assignee control (internal) ----
   Two ways to assign, in one menu: a debounced internal-user search (a person),
   and the JOB FUNCTIONS section (SOUND-667) that hands the pick to the server's
   resolver. The search box filters both. */
function AssigneeMenu({
  assignee,
  authUser,
  companyId,
  onAssign,
  functions = [],
  onAssignFunction,
  assigningFunction,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!companyId) return;
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("userType", "internal");
        params.set("limit", "10");
        if (query) params.set("search", query);
        const res = await makeApiRequest(
          "get",
          `${apiRoutes.getCompanyUsers}?${params.toString()}`,
          authUser,
          companyId
        );
        setResults(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open, companyId]);

  const pick = (u) => {
    setOpen(false);
    setQuery("");
    onAssign({
      userId: u._id || u.id || u.uid,
      displayName: displayNameOf(u),
      email: u.email,
    });
  };

  // The users endpoint already applies `?search=`; run the same client-side pass
  // so both sections react to the box identically (and instantly, before the
  // 250ms debounce lands).
  const userRows = filterUserTargets(results, query);
  const functionRows = filterFunctionTargets(functions, query);

  const pickFunction = (fn) => {
    setOpen(false);
    setQuery("");
    onAssignFunction?.(fn);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        className="btn btn-outline"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {assignee ? (
          <span className="inline-flex items-center gap-1.5">
            <UserAvatar user={{ name: assignee.displayName, email: assignee.email }} />
            <span>{(assignee.displayName || "").split(" ")[0] || "Assigned"}</span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
            <UserPlus className="h-4 w-4" />
            Assign
          </span>
        )}
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] z-30 w-72 animate-scale-in rounded-lg border border-border bg-card p-1.5 shadow-lg"
        >
          <div className="relative px-1 pb-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search team or function…"
              className="input h-8 pl-8 text-sm"
            />
          </div>
          <div className="max-h-72 overflow-y-auto">
            {loading ? (
              <div className="py-3 text-center text-xs text-muted-foreground">Searching…</div>
            ) : userRows.length === 0 ? (
              <div className="py-3 text-center text-xs text-muted-foreground">
                {query ? "No team members found." : "Start typing to search."}
              </div>
            ) : (
              userRows.map((u) => (
                <button
                  key={u._id || u.id || u.email}
                  role="menuitem"
                  onClick={() => pick(u)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-muted"
                >
                  <UserAvatar user={u} />
                  <span className="min-w-0 flex-1 truncate text-sm">{displayNameOf(u)}</span>
                </button>
              ))
            )}

            {/* ── JOB FUNCTIONS (SOUND-667) ──
                Assigning to a function hands the pick to the server's resolver.
                Every row states WHERE IT WILL LAND before the click — the owner's
                name, or the capacity count for a least-loaded queue — because
                nobody should have to guess who they just handed work to.
                An unresolvable function is shown DISABLED rather than hidden: a
                missing row is a mystery, a disabled row is a diagnosis. */}
            {functionRows.length > 0 && (
              <>
                <div className="flex items-center gap-2 px-2 pb-1 pt-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Job functions
                  </span>
                  <span className="h-px flex-1 bg-border" />
                </div>
                {functionRows.map((fn) => {
                  const target = describeFunctionTarget(fn);
                  const pending = assigningFunction === fn.functionKey;
                  return (
                    <button
                      key={fn.functionKey}
                      role="menuitem"
                      disabled={!target.resolvable || pending}
                      title={
                        target.resolvable
                          ? `Assigns to ${target.hint}`
                          : `${fn.label} has no owner or active members — set one in Settings › Job Functions`
                      }
                      onClick={() => pickFunction(fn)}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left enabled:hover:bg-muted disabled:cursor-not-allowed disabled:opacity-55"
                    >
                      <Briefcase className="h-4 w-4 shrink-0 text-brand" />
                      <span className="min-w-0 flex-1 truncate text-sm">{fn.label}</span>
                      <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
                        {pending ? (
                          "Assigning…"
                        ) : target.via === "capacity" ? (
                          <>
                            <Scale className="h-3 w-3" />
                            {target.activeHolderCount}
                          </>
                        ) : (
                          <span className="max-w-[7.5rem] truncate">{target.hint}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
          {assignee && (
            <>
              <div className="my-1 h-px bg-border" />
              <button
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onAssign(null);
                }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-muted"
              >
                Unassign
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default requireAuth(Ticket);
